import { useSelector } from "react-redux";
import { SummaryDataProvider } from "~/features/configurator/Shaft/SummaryDataProvider";
import type { RootState } from "~/app/store";
import { useXmlConfiguratorTranslations } from "~/features/configurator/common/useXmlConfiguratorTranslations";
import { getStep1SummaryData, getStep2SummaryData } from "~/features/configurator/Shaft/inch/InchSummaryDataProvider";

import {
  OnStepChangeContextProvider,
  ShaftSummaryStepFrame,
} from "~/features/configurator/Shaft/components/ShaftSummaryStepFrame";
import {
  renderSummary,
  renderValue,
  SummaryStepContainer,
  SummaryProviderContextProvider,
  Table,
  useSummaryProvider,
} from "~/features/configurator/Shaft/inch/components/ShaftSummaryDataHelper";
import { getHighestVisitedStep } from "~/features/configurator/Linear/redux/selectors";
import { removePTags } from "~/common/utils/domUtils";

interface Props {
  isFullReview: boolean;
  onStepChange?: () => void;
}

export const InchShaftSummaryData = ({ onStepChange }: Props) => {
  const xmlT = useXmlConfiguratorTranslations().t!;

  const summaryProvider = useSelector((state: RootState) => {
    return new SummaryDataProvider(state.linearConfigurator, xmlT);
  });

  const highestVisitedStep = useSelector(getHighestVisitedStep);

  return (
    <OnStepChangeContextProvider value={onStepChange}>
      <SummaryProviderContextProvider value={summaryProvider}>
        <SummaryStepContainer>
          {highestVisitedStep >= 0 && <Step1Summary />}
          {highestVisitedStep >= 1 && <Step2Summary />}
        </SummaryStepContainer>
      </SummaryProviderContextProvider>
    </OnStepChangeContextProvider>
  );
};

const Step1Summary = () => {
  const xmlT = useXmlConfiguratorTranslations().t!;
  const summaryProvider = useSummaryProvider();

  const step1Data = getStep1SummaryData(summaryProvider) ?? [];

  return (
    <ShaftSummaryStepFrame stepNumber={1} name={xmlT("configurator.shaft.step.1.title")}>
      <Table>
        {step1Data.map((data) => (
          <tbody key={data.name?.toString()}>
            <tr>
              <td colSpan={2}>
                <div>{data.name}</div>
              </td>
              <td colSpan={2}>
                <div>{renderValue(data)}</div>
              </td>
              <td colSpan={2} />
            </tr>
          </tbody>
        ))}
      </Table>
    </ShaftSummaryStepFrame>
  );
};

const Step2Summary = () => {
  const xmlT = useXmlConfiguratorTranslations().t!;
  const summaryProvider = useSummaryProvider();

  const step2Data = getStep2SummaryData(summaryProvider);
  return (
    <ShaftSummaryStepFrame stepNumber={2} name={xmlT("configurator.shaft.step.2.title")}>
      <Table>
        {step2Data.length && (
          <tbody key={step2Data.length.name?.toString()}>
            <tr>
              <td colSpan={1} dangerouslySetInnerHTML={{ __html: removePTags(step2Data.length.name as string)! }} />
              <td colSpan={1}>{renderValue(step2Data.length)}</td>
              <td
                colSpan={1}
                dangerouslySetInnerHTML={{ __html: removePTags(step2Data.numberOfParts?.name as string)! }}
              />
              <td colSpan={1}>{renderValue(step2Data.numberOfParts)}</td>
              <td colSpan={2} />
            </tr>
          </tbody>
        )}
        {step2Data.tolerance && (
          <tbody key={step2Data.tolerance?.name?.toString()}>
            <tr>
              <td colSpan={1} dangerouslySetInnerHTML={{ __html: removePTags(step2Data.tolerance?.name as string)! }} />
              <td colSpan={1}>{renderValue(step2Data.tolerance)}</td>
              <td colSpan={4}>{step2Data.tolerance?.extra}</td>
            </tr>
          </tbody>
        )}
        {step2Data.left && (
          <tbody>
            <tr>
              <td dangerouslySetInnerHTML={{ __html: removePTags(step2Data.left.name as string)! }} />
              <td>{step2Data.left.value}</td>
              <td>{step2Data.left.extra}</td>
              <td colSpan={3} />
            </tr>
            {step2Data.left.chamfer && (
              <tr>
                <td colSpan={2} />
                {renderSummary(step2Data.left.chamfer.angle)}
                {renderSummary(step2Data.left.chamfer.width)}
              </tr>
            )}
          </tbody>
        )}
        {step2Data.middle && (
          <tbody>
            <tr>
              <td dangerouslySetInnerHTML={{ __html: removePTags(step2Data.middle.name as string)! }} />
              <td>{step2Data.middle.value}</td>
              {step2Data.middle.radialThread && (
                <>
                  {renderSummary(step2Data.middle.radialThread.pdt)}
                  {renderSummary(step2Data.middle.radialThread.threadSpacing)}
                </>
              )}
              {!step2Data.middle.radialThread && <td colSpan={4} />}
            </tr>
            {step2Data.middle.radialThread && (
              <>
                <tr>
                  <td colSpan={2} />
                  {renderSummary(step2Data.middle.radialThread.threadDepth)}
                  {renderSummary(step2Data.middle.radialThread.aL)}
                </tr>
                <tr>
                  <td colSpan={2} />
                  {renderSummary(step2Data.middle.radialThread.threadHoleAmount)}
                  {renderSummary(step2Data.middle.radialThread.aR)}
                </tr>
              </>
            )}
          </tbody>
        )}
        {step2Data.right && (
          <tbody>
            <tr>
              <td dangerouslySetInnerHTML={{ __html: removePTags(step2Data.right.name as string)! }} />
              <td>{step2Data.right.value}</td>
              <td>{step2Data.right.extra}</td>
              <td colSpan={3} />
            </tr>
            {step2Data.right.chamfer && (
              <tr>
                <td colSpan={2} />
                {renderSummary(step2Data.right.chamfer.angle)}
                {renderSummary(step2Data.right.chamfer.width)}
              </tr>
            )}
          </tbody>
        )}

        <tbody>
          <tr></tr>
        </tbody>
        <tbody>
          <tr></tr>
        </tbody>
      </Table>
    </ShaftSummaryStepFrame>
  );
};
